import React from "react";
import { MaskedLines, Reveal, FadeIn } from "./Reveal";
import { PrimaryButton, GhostButton } from "./Buttons";

// Reusable inner-page hero (ivory, editorial)
export const PageHero = ({ eyebrow, titleLines, lede }) => (
  <section className="pt-40 md:pt-52 pb-16 md:pb-24">
    <div className="yadid-container">
      {eyebrow && (
        <FadeIn delay={0.1}>
          <p className="eyebrow text-brass mb-8">{eyebrow}</p>
        </FadeIn>
      )}
      <MaskedLines
        lines={titleLines}
        delay={0.2}
        className="font-serif text-ink text-[2.4rem] sm:text-5xl lg:text-[3.7rem] xl:text-[4.1rem] leading-[1.04] tracking-[-0.02em] max-w-5xl"
      />
      {lede && (
        <Reveal delay={0.5} className="mt-10 max-w-2xl">
          <p className="text-lg md:text-xl text-ink/70 font-light leading-relaxed">{lede}</p>
        </Reveal>
      )}
    </div>
  </section>
);

export const SectionEyebrow = ({ children, className = "", dark = false }) => (
  <p className={`eyebrow mb-6 ${dark ? "text-brass" : "text-brass"} ${className}`}>{children}</p>
);

// Closing invitation band
export const ClosingCTA = ({
  quote = "Some conversations are worth having before the path is clear.",
  sub = "When the moment matters, the first step may simply be a trusted conversation.",
}) => (
  <section className="bg-ivory border-t border-stone">
    <div className="yadid-container py-28 md:py-40 text-center flex flex-col items-center">
      <Reveal>
        <h2 className="font-serif text-3xl md:text-5xl lg:text-[3.9rem] leading-[1.06] tracking-[-0.02em] text-ink max-w-4xl mx-auto">
          {quote}
        </h2>
      </Reveal>
      <Reveal delay={0.15}>
        <p className="mt-8 text-ink/60 font-light text-lg max-w-xl mx-auto">{sub}</p>
      </Reveal>
      <Reveal delay={0.25}>
        <div className="mt-12 flex flex-col sm:flex-row items-center gap-4">
          <PrimaryButton to="/contact" data-testid="closing-cta-primary">
            Start a Conversation
          </PrimaryButton>
          <GhostButton to="/how-we-advise">How We Advise</GhostButton>
        </div>
      </Reveal>
    </div>
  </section>
);

// Slow editorial marquee
export const Marquee = ({ words }) => {
  const set = [...words, ...words];
  return (
    <div className="relative overflow-hidden border-y border-stone bg-ivory">
      {/* soft edge fades so words emerge and dissolve into the page */}
      <div className="pointer-events-none absolute inset-y-0 left-0 w-24 md:w-56 bg-gradient-to-r from-ivory via-ivory/80 to-transparent z-10" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-24 md:w-56 bg-gradient-to-l from-ivory via-ivory/80 to-transparent z-10" />
      <div className="marquee-track py-9 md:py-12" style={{ animationDuration: "60s" }}>
        {set.map((w, i) => (
          <span key={i} className="inline-flex items-center">
            <span className="font-serif italic text-3xl md:text-[2.75rem] leading-none text-ink/85 px-10 md:px-16">
              {w}
            </span>
            <span className="h-[7px] w-[7px] rotate-45 bg-brass shrink-0" aria-hidden="true" />
          </span>
        ))}
      </div>
    </div>
  );
};
